import { Bus, Car, Train } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type TransportStopFeature = GeoJSON.Feature<
  GeoJSON.Point,
  {
    id?: number;
    name?: string;
    class?: string;
    type?: string;
    color?: string;
  }
>;

export const NEARBY_TRANSPORT_RADIUS_M = 2000;

export function haversineMetres(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function fmtDist(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

export function walkMins(m: number): number {
  return Math.max(1, Math.round(m / 83.3));
}

export function stopTypeLabel(type?: string): string {
  if (type === 'taxi' || type === 'taxi_rank') return 'Taxi rank';
  if (type === 'rail_station') return 'Rail station';
  if (type === 'bus_station' || type === 'trotro') return 'Trotro / bus station';
  if (type === 'bus_stop') return 'Bus stop';
  if (type === 'lorry_park') return 'Lorry park';
  return type?.replace(/_/g, ' ') || 'Transit stop';
}

export function stopIcon(type?: string): LucideIcon {
  if (type === 'taxi' || type === 'taxi_rank') return Car;
  if (type === 'rail_station') return Train;
  return Bus;
}

export function stopColorClass(type?: string): string {
  if (type === 'taxi' || type === 'taxi_rank') {
    return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
  }
  if (type === 'rail_station') {
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
  }
  return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
}

export function sortStopsByDistance(
  stops: TransportStopFeature[],
  anchor: { lat: number; lng: number } | null | undefined,
): TransportStopFeature[] {
  if (!anchor) return stops;
  return [...stops].sort((a, b) => {
    const [aLng, aLat] = a.geometry.coordinates;
    const [bLng, bLat] = b.geometry.coordinates;
    return (
      haversineMetres(anchor.lat, anchor.lng, aLat, aLng) -
      haversineMetres(anchor.lat, anchor.lng, bLat, bLng)
    );
  });
}
